// A container, so the layout follows the width it is given, not the window's.
.explorer {
  container-type: inline-size;
  position: relative;
}

// The sizes are custom properties set from UploadFlow.tsx: --row (a box), --gap (between boxes),
// --pad (inside a column's frame), --caption (the frame's caption) and --column (the diagram's
// height), so the connectors can end at each box's middle. A column shorter than the
// diagram is pushed down to its middle by padding set on it.
.flow {
  position: relative;
  display: grid;
  grid-template-columns:
    11rem minmax(2.5rem, 1fr) 15rem minmax(2.5rem, 1fr) 7.5rem minmax(2.5rem, 1fr)
    13rem;
  align-items: start;
}

.column {
  box-sizing: border-box;
  height: var(--column);
}

.connector,
.hub {
  align-self: center;
}

// The ways that start from one place, under a caption saying where. Unframed: the boxes stand on
// their own. The padding keeps the boxes where the connectors expect them.
.group {
  padding: 0 var(--pad) var(--pad);
}

.group + .group {
  margin-top: var(--gap);
}

.caption {
  display: flex;
  height: var(--caption);
  align-items: center;
  margin: 0;
  padding: 0 0.5rem;
  color: var(--color-code-foreground);
  font-size: 0.75rem;
  font-weight: 600;
}

.list {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  margin: 0;
  padding: 0;
  list-style: none;

}


.node {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.75rem;
  height: var(--row);
  padding: 0 1rem;
  font: inherit;
  text-align: start;
  cursor: pointer;
  border: 1px solid var(--color-code-border);
  border-radius: var(--radius-lg);
  background: var(--color-code-background-raised);
  color: var(--color-code-foreground);
  font-weight: 500;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;

  // `data-hinted` is the click hint's pointer resting on it.
  &:hover,
  &[data-hinted],
  &:focus-visible {
    border-color: color-mix(in srgb, var(--color-code-constant) 45%, var(--color-code-border));
  }

  // A box whose way is in use: outlined in the route's colour.
  &[aria-pressed="true"] {
    border-color: var(--color-code-constant);
    background: color-mix(in srgb, var(--color-code-constant) 10%, var(--color-code-background-raised));
  }
}

.nodeIcon {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  color: var(--color-code-constant);

  > svg {
    width: 1.375rem;
    height: 1.375rem;
  }
}

// The route in use: the one line that moves.
@mixin route {
  stroke: var(--color-code-constant);
  stroke-width: 2.5;
  stroke-dasharray: 3 7;
  animation: flow 1200ms linear infinite;
  animation-play-state: paused;
}

// Stretches to the diagram's height, so the paths' fractions are fractions of the boxes' rows.
.connector {
  align-self: stretch;
  min-width: 0;

  svg {
    display: block;
    width: 100%;
    height: var(--column);
    overflow: visible;
  }

  // The ways not in use: quiet, still hairlines.
  path {
    fill: none;
    stroke: color-mix(in srgb, var(--color-code-foreground) 16%, transparent);
    stroke-width: 1.25;
    stroke-linecap: round;
    vector-effect: non-scaling-stroke;
  }

  // The route in use: the one line that moves.
  path[data-active] {
    @include route;
  }
}

// Where the columns stack, the route in use is measured and drawn over the whole diagram.
.stackedRoute {
  display: none;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;

  path {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    @include route;
  }
}

[data-playing] .connector path[data-active],
[data-playing] .stackedRoute path {
  animation-play-state: running;
}

@keyframes flow {
  to {
    stroke-dashoffset: -20;
  }
}

.hub {
  transform: translateY(calc((var(--caption) - var(--pad)) / 2));
  position: relative;
  display: grid;
  width: 7.5rem;
  aspect-ratio: 1;
  place-items: center;
}

// CalmLens in flat colour: a solid disc, no gradient or glow.
.hubRing {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--color-primary);
}

.hubLogo {
  position: relative;
  font-size: 3.5rem !important;
}

@media (prefers-reduced-motion: reduce) {
  .connector path,
  .stackedRoute path {
    animation: none;
  }
}

.example {
  // Examples differ in length: keep the page from jumping as they change.
  min-height: 24rem;
  margin-top: 2.5rem;
}

// An example is one or more blocks, each set apart.
.block + .block {
  margin-top: 1.5rem;
}

.exampleHeader {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1.5rem;
  margin-bottom: 0.75rem;
}

// The ways whose code is shown, each a link to its guide, joined by plus signs.
.exampleTitle {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.5rem;
}

// The guide for the code, in the body's size and weight.
.exampleLink {
  font-size: 1rem;
  font-weight: 400;
}

// Where the code runs.
.side {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding-block: 0.375rem;
  padding-inline: 0.75rem 0.875rem;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  font-size: 0.9375rem;
  font-weight: 600;

  svg {
    flex-shrink: 0;
    color: var(--color-code-constant);
  }
}

// A narrower column keeps the diagram as it is, with smaller boxes.
@container (max-width: 940px) {
  .flow {
    grid-template-columns:
      9rem minmax(1.25rem, 1fr) 11.5rem minmax(1.25rem, 1fr) 5.5rem minmax(1.25rem, 1fr)
      10.5rem;
  }

  .hub {
    width: 5.5rem;
  }

  .hubLogo {
    font-size: 2.5rem !important;
  }

  .node {
    padding: 0 0.75rem;
    font-size: 0.875rem;
  }
}

// Below this the columns stack: file type, upload method, CalmLens and decision, two boxes to a
// row, with the route in use measured and drawn down the gutter between them.
@container (max-width: 640px) {
  .flow {
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
    gap: 0;
  }

  .column {
    width: 100%;
    height: auto;
    padding-top: 0 !important;
  }

  .list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 1.25rem;
    row-gap: 0.5rem;
  }

  // Spacers between the stacked columns.
  .connector {
    align-self: auto;
    height: 2rem;

    svg {
      display: none;
    }
  }

  .hub {
    transform: none;
  }

  .stackedRoute {
    display: block;
  }
}
